import type { ISODate } from "../types.js";

/**
 * Minimal date helpers that work on YYYY-MM-DD strings and never touch the
 * local timezone (all math is done in UTC on the calendar date).
 */

const pad = (n: number): string => String(n).padStart(2, "0");

export function toISODate(y: number, m: number, d: number): ISODate {
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function parseISODate(s: ISODate): { y: number; m: number; d: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) throw new Error(`invalid ISO date: ${s}`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

function toUTC(s: ISODate): number {
  const { y, m, d } = parseISODate(s);
  return Date.UTC(y, m - 1, d);
}

function fromUTC(ms: number): ISODate {
  const dt = new Date(ms);
  return toISODate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

export function addDays(s: ISODate, days: number): ISODate {
  return fromUTC(toUTC(s) + days * 86_400_000);
}

/** Calendar days from `a` to `b` (b - a). */
export function diffDays(a: ISODate, b: ISODate): number {
  return Math.round((toUTC(b) - toUTC(a)) / 86_400_000);
}

/** 0 = Sunday ... 6 = Saturday */
export function dayOfWeek(s: ISODate): number {
  return new Date(toUTC(s)).getUTCDay();
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function lastDayOfMonth(y: number, m: number): ISODate {
  return toISODate(y, m, daysInMonth(y, m));
}

export function compareISODate(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** n-th (1-based) given weekday of a month, e.g. 2nd Monday of January. */
export function nthWeekdayOfMonth(y: number, m: number, weekday: number, n: number): ISODate {
  const first = toISODate(y, m, 1);
  const offset = (weekday - dayOfWeek(first) + 7) % 7;
  return addDays(first, offset + (n - 1) * 7);
}
